/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {remCalc} from '../../theme/_utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isPecualiarAddressFormCountry} from '../../utils/peculiar-address-form'

const JPAddressDisplay = ({
    address,
    styles,
    confirmationGap,
    nameStyle,
    isConfirmation,
    isBilling,
    isAccount,
}) => {
    return (
        <Flex {...styles.addressContainer} style={confirmationGap}>
            {address?.c_fiscalCode && <Text mb={remCalc(16)}>{address.c_fiscalCode}</Text>}
            {address?.c_companyName && <Text>{address.c_companyName}</Text>}
            {address?.c_vatNumber && <Text>{address.c_vatNumber}</Text>}
            {address?.c_sdi && <Text>{address.c_sdi}</Text>}
            {(address?.companyName || address?.c_vatNumber || address?.c_sdi) && <>&nbsp;</>}
            <Text style={nameStyle}>
                {isConfirmation && !isBilling && !isAccount ? (
                    <>
                        {address?.firstName} {address?.lastName}
                    </>
                ) : (
                    <>
                        {address?.lastName} {address?.firstName}
                    </>
                )}
            </Text>
            <Text>{address?.phone}</Text>
            <Text>{address?.c_countryName}</Text>
            <Text>{address?.postalCode}</Text>
            <Text>
                {' '}
                {address?.c_regionName}, {address?.city}{' '}
            </Text>
            <Text>{address?.address1}</Text>
        </Flex>
    )
}

const DefaultAddressDisplay = ({address, styles, confirmationGap, nameStyle}) => (
    <Flex {...styles.addressContainer} style={confirmationGap}>
        {address?.c_fiscalCode && <Text mb={remCalc(16)}>{address.c_fiscalCode}</Text>}
        {address?.c_companyName && <Text>{address.c_companyName}</Text>}
        {address?.c_vatNumber && <Text>{address.c_vatNumber}</Text>}
        {address?.c_sdi && <Text>{address.c_sdi}</Text>}
        {(address?.c_companyName || address?.c_vatNumber || address?.c_sdi) && <>&nbsp;</>}
        <Text style={nameStyle}>
            {address?.firstName} {address?.lastName}
        </Text>
        <Text>{address?.phone}</Text>
        <Text>{address?.address1}</Text>
        {address?.address2 && <Text>{address?.address2}</Text>}
        <Text>
            {address?.city}, {address?.c_regionName}, {address?.postalCode}
        </Text>
    </Flex>
)

const AddressDisplay = ({
    address,
    isConfirmation = false,
    isTitleBold = false,
    isBilling = false,
    isAccount = false,
}) => {
    const nameStyle = isConfirmation && isTitleBold ? {fontWeight: 'bold'} : {}
    const confirmationGap = isConfirmation ? {gap: remCalc(4)} : {}
    const styles = useMultiStyleConfig('AddressDisplay', {})
    const {locale} = useMultiSite()
    const isPeculiarAddressFormCountry = isPecualiarAddressFormCountry(locale)
    return isPeculiarAddressFormCountry ? (
        <JPAddressDisplay
            address={address}
            styles={styles}
            confirmationGap={confirmationGap}
            nameStyle={nameStyle}
            isConfirmation={isConfirmation}
            isBilling={isBilling}
            isAccount={isAccount}
        />
    ) : (
        <DefaultAddressDisplay
            address={address}
            styles={styles}
            confirmationGap={confirmationGap}
            nameStyle={nameStyle}
        />
    )
}

JPAddressDisplay.propTypes = {
    address: PropTypes.object,
    styles: PropTypes.object,
    confirmationGap: PropTypes.object,
    nameStyle: PropTypes.object,
    isConfirmation: PropTypes.bool,
    isBilling: PropTypes.bool,
    isAccount: PropTypes.bool,
}

DefaultAddressDisplay.propTypes = {
    address: PropTypes.object,
    styles: PropTypes.object,
    confirmationGap: PropTypes.object,
    nameStyle: PropTypes.object,
}

AddressDisplay.propTypes = {
    address: PropTypes.object,
    isConfirmation: PropTypes.bool,
    isTitleBold: PropTypes.bool,
    isBilling: PropTypes.bool,
    isAccount: PropTypes.bool,
}

export default AddressDisplay
